<template>
  <v-dialog
    :value="value"
    max-width="440px"
    :retain-focus="false"
    :persistent="isSubmitting"
    @input="handleDialogInput"
  >
    <v-card
      data-testid="password-change-dialog"
      style="border-radius: 16px; padding: 22px 28px 24px"
    >
      <div
        class="d-flex align-center justify-space-between"
        style="margin-bottom: 20px"
      >
        <div style="font-size: 18px; font-weight: 600; line-height: 24px; color: #0D0E17">
          비밀번호 변경
        </div>
        <div
          data-testid="password-change-close-btn"
          class="default-icon-btn d-flex align-center justify-center"
          :aria-disabled="isSubmitting"
          style="width: 32px; height: 32px"
          @click="close"
        >
          <v-icon style="font-size: 20px; color: #8A8DA8">
            mdi-close
          </v-icon>
        </div>
      </div>

      <div
        v-if="errors.form"
        data-testid="password-change-form-error"
        class="password-change-form-error"
      >
        {{ errors.form }}
      </div>

      <div class="password-change-field">
        <label
          for="password-change-current-input"
          data-testid="password-change-current-label"
          class="password-change-label"
        >
          현재 비밀번호
        </label>
        <div
          class="password-change-input-wrap"
          :class="{ 'password-change-input-error': errors.currentPassword }"
        >
          <input
            id="password-change-current-input"
            v-model="form.currentPassword"
            data-testid="password-change-current-input"
            :type="showCurrentPassword ? 'text' : 'password'"
            autocomplete="current-password"
            maxlength="128"
            spellcheck="false"
            :aria-invalid="Boolean(errors.currentPassword)"
            placeholder="현재 비밀번호를 입력해 주세요."
            @input="clearFieldError('currentPassword')"
            @keyup.enter="submit"
          >
          <div
            class="default-icon-btn d-flex align-center justify-center password-visibility-btn"
            @mousedown.prevent
            @click="showCurrentPassword = !showCurrentPassword"
          >
            <v-icon style="font-size: 20px; color: #8A8DA8">
              {{ showCurrentPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
            </v-icon>
          </div>
        </div>
        <div
          v-if="errors.currentPassword"
          data-testid="password-change-current-error"
          class="password-change-error"
        >
          {{ errors.currentPassword }}
        </div>
      </div>

      <div class="password-change-field">
        <label
          for="password-change-new-input"
          data-testid="password-change-new-label"
          class="password-change-label"
        >
          새 비밀번호
        </label>
        <div
          class="password-change-input-wrap"
          :class="{ 'password-change-input-error': errors.newPassword }"
        >
          <input
            id="password-change-new-input"
            v-model="form.newPassword"
            data-testid="password-change-new-input"
            :type="showNewPassword ? 'text' : 'password'"
            autocomplete="new-password"
            maxlength="64"
            spellcheck="false"
            :aria-invalid="Boolean(errors.newPassword)"
            placeholder="새 비밀번호를 입력해 주세요."
            @input="clearFieldError('newPassword')"
            @keyup.enter="submit"
          >
          <div
            class="default-icon-btn d-flex align-center justify-center password-visibility-btn"
            @mousedown.prevent
            @click="showNewPassword = !showNewPassword"
          >
            <v-icon style="font-size: 20px; color: #8A8DA8">
              {{ showNewPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
            </v-icon>
          </div>
        </div>
        <div
          v-if="errors.newPassword"
          data-testid="password-change-new-error"
          class="password-change-error"
        >
          {{ errors.newPassword }}
        </div>
      </div>

      <div class="password-change-policy-message">
        {{ passwordRequirementsMessage }}
      </div>

      <div class="password-change-field" style="margin-bottom: 24px">
        <label
          for="password-change-confirm-input"
          data-testid="password-change-confirm-label"
          class="password-change-label"
        >
          새 비밀번호 확인
        </label>
        <div
          class="password-change-input-wrap"
          :class="{ 'password-change-input-error': errors.newPasswordConfirm }"
        >
          <input
            id="password-change-confirm-input"
            v-model="form.newPasswordConfirm"
            data-testid="password-change-confirm-input"
            :type="showNewPasswordConfirm ? 'text' : 'password'"
            autocomplete="new-password"
            maxlength="64"
            spellcheck="false"
            :aria-invalid="Boolean(errors.newPasswordConfirm)"
            placeholder="새 비밀번호를 한 번 더 입력해 주세요."
            @input="clearFieldError('newPasswordConfirm')"
            @keyup.enter="submit"
          >
          <div
            class="default-icon-btn d-flex align-center justify-center password-visibility-btn"
            @mousedown.prevent
            @click="showNewPasswordConfirm = !showNewPasswordConfirm"
          >
            <v-icon style="font-size: 20px; color: #8A8DA8">
              {{ showNewPasswordConfirm ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
            </v-icon>
          </div>
        </div>
        <div
          v-if="errors.newPasswordConfirm"
          data-testid="password-change-confirm-error"
          class="password-change-error"
        >
          {{ errors.newPasswordConfirm }}
        </div>
      </div>

      <div
        data-testid="password-change-submit-btn"
        class="default-theme-btn d-flex align-center justify-center"
        :class="{ 'disabled-theme-btn': isSubmitting }"
        :aria-disabled="isSubmitting"
        style="width: 100%; height: 40px"
        @click="submit"
      >
        <span style="font-size: 14px; font-weight: 600; line-height: 18px">
          {{ isSubmitting ? '변경 중' : '비밀번호 변경' }}
        </span>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { PASSWORD_REQUIREMENTS_MESSAGE } from '@/utils/auth-security-policy.mjs'
import {
  createAuthenticatedPasswordChangeSubmitter,
} from '@/features/authenticated-password-change/authenticated-password-change-domain.mjs'
import {
  changeAuthenticatedPassword,
} from '@/features/authenticated-password-change/authenticated-password-change-service'

function createEmptyForm() {
  return {
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  }
}

function createEmptyErrors() {
  return {
    form: '',
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  }
}

export default {
  name: 'AuthenticatedPasswordChangeDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: createEmptyForm(),
      errors: createEmptyErrors(),
      showCurrentPassword: false,
      showNewPassword: false,
      showNewPasswordConfirm: false,
      isSubmitting: false,
      passwordRequirementsMessage: PASSWORD_REQUIREMENTS_MESSAGE,
      submitter: createAuthenticatedPasswordChangeSubmitter(changeAuthenticatedPassword),
    }
  },
  watch: {
    value(isOpen) {
      if (!isOpen) this.reset()
    },
  },
  methods: {
    reset() {
      this.form = createEmptyForm()
      this.errors = createEmptyErrors()
      this.showCurrentPassword = false
      this.showNewPassword = false
      this.showNewPasswordConfirm = false
    },
    handleDialogInput(isOpen) {
      if (!isOpen) this.close()
    },
    close() {
      if (this.isSubmitting) return
      this.reset()
      this.$emit('input', false)
    },
    clearFieldError(field) {
      this.errors[field] = ''
      this.errors.form = ''
    },
    async submit() {
      if (this.isSubmitting) return

      this.isSubmitting = true
      try {
        const result = await this.submitter.submit(this.form)
        if (result.status === 'validation-error' || result.status === 'request-error') {
          this.errors = result.errors
          return
        }
        if (result.status === 'success') {
          this.reset()
          this.$emit('input', false)
          this.$emit('changed')
        }
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.password-change-field {
  margin-bottom: 16px;
}

.password-change-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: #414458;
}

.password-change-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  border: 1px solid #E8EAF2;
  border-radius: 8px;
  background-color: #FFFFFF;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.password-change-input-wrap:focus-within {
  border-color: #687EFF;
  box-shadow: 0 0 0 2px rgba(104, 126, 255, 0.15);
}

.password-change-input-wrap.password-change-input-error {
  border-color: #FF3B30;
}

.password-change-input-wrap input {
  min-width: 0;
  flex: 1;
  height: 100%;
  border: 0;
  outline: 0;
  padding: 0 4px 0 12px;
  background: transparent;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #414458;
}

.password-change-input-wrap input::placeholder {
  color: #ABAEC4;
}

.password-visibility-btn {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  margin-right: 2px;
}

.password-change-error {
  margin-top: 5px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #FF3B30;
}

.password-change-form-error {
  margin-bottom: 16px;
  border-radius: 8px;
  padding: 10px 12px;
  background-color: #FFF1F0;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #D92D20;
}

.password-change-policy-message {
  margin: -8px 2px 16px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #8A8DA8;
}
</style>
